import React, { useState, useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import Search from './search';
import { Menu } from 'antd';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import MyAvatar from './myAvatar';
import { ArrowLeftOutlined, SettingOutlined } from '@ant-design/icons';
import { addOpacityMenu } from 'src/utils/colorHelpers';
import { pingAPI } from 'src/actions/maintenanceActions';
import paLogo from 'img/PA_Logo_white.svg';

const useStyles = createUseStyles({
	menu: (props) => ({
		lineHeight: '64px',
		display: 'flex',
		position: 'fixed',
		width: '100%',
		left: '0',
		top: '0',
		right: '0',
		height: '64px',
		zIndex: '1000',
		'& .ant-menu-item': {
			'&:hover': {
				background: `${props.model_TopBack} !important`,
				color: `${props.model_TopText} !important`,
			},
			color: `${addOpacityMenu(props.model_TopText)} !important`,
		},
		'& .ant-menu-item-selected': {
			background: `${props.model_TopBack} !important`,
			color: `${props.model_TopText} !important`,
		},
	}),
	textColor: {
		color: 'inherit !important',
	},
	rightItems: {
		float: 'right',
	},
	leftItems: {
		float: 'left',
	},
	search: {
		display: 'flex !important',
		justifyContent: 'center !important',
		alignItems: 'center !important',
		margin: '0 auto !important',
	},

	connectionStatusSpan: {
		color: 'red',
		animation: '$blinkingText 3s infinite',
	},
	'@keyframes blinkingText': {
		'0%': { color: 'red' },
		'49%': { color: 'red' },
		'60%': { color: 'transparent' },
		'99%': { color: 'transparent' },
		'100%': { color: 'red' },
	},
	connectionStatus: {
		cursor: 'default',
	},
	logo: {
		position: 'relative',
		top: '8px',
	},
	searchSpace: {
		display: 'flex',
		width: '100%',
	},
	space: {
		width: '54.83px',
		height: '100%',
	},
});

const NormalNavbar = (props) => {
	const { t } = useTranslation();
	const params = useParams();

	const { modelList, activeUser, modelView = false, projectView, activeModel, settings } = props;
	const { user } = activeUser;
	const { model } = activeModel;
	const { homeView, slug } = model;

	const { menuColor: colors } = settings;

	const classes = useStyles(colors);

	const [apiOnline, setApiOnline] = useState(true);
	const [clientOnline, setClientOnline] = useState(window.navigator.onLine);

	const activeTab = `models:${params.modelId}`;
	const location = useLocation();

	// ping the api every 30s to know if it is still up
	useEffect(() => {
		const interval = setInterval(() => {
			pingAPI().then((apiState) => {
				setApiOnline(apiState);
				setClientOnline(window.navigator.onLine);
			});
		}, 30000);

		return () => clearInterval(interval); // This represents the unmount function, in which you need to clear your interval to prevent memory leaks.
	}, []);

	const adminViewLink =
		activeModel.model._id !== '' ? `/adminView/models/${activeModel.model._id}/index` : `/adminView/addModel`;

	const displayConnectionStatus = () => {
		if (clientOnline && apiOnline) {
			return null;
		}
		let connectionStatus = '';
		if (!apiOnline) {
			connectionStatus = t('maintenance.apiOffline', '*API offline!');
		}
		if (!clientOnline) {
			connectionStatus = t('maintenance.clientOffline', '*You are offline!');
		}

		return (
			<Menu.Item className={`${classes.leftItems} ${classes.connectionStatus}`} key='connectionStatus'>
				<span className={classes.connectionStatusSpan}> {connectionStatus}</span>
			</Menu.Item>
		);
	};

	const activeUserItem =
		activeUser && activeUser.user && activeUser.user.adminAccess
			? {
					label: (
						<Link to={adminViewLink} className={classes.textColor}>
							<SettingOutlined />
							<span>{t('navbar.admin', 'Admin Mode')} </span>
						</Link>
					),
					className: classes.rightItems,
					key: '/admin-view/',
			  }
			: null;

	const modelOrProjectView =
		modelView || projectView
			? {
					label: (
						<div className={classes.searchSpace}>
							<Search />
						</div>
					),
					key: 'navigation-item',
					className: `${classes.search}`,
			  }
			: {
					label: <span />,
					key: 'navigation-item',
					className: `${classes.search}`,
			  };

	const projectItem = projectView
		? {
				label: (
					<Link
						to={slug !== '' && homeView !== '' ? `/model/${slug}/view/${homeView}` : '/'}
						className={classes.textColor}
					>
						<ArrowLeftOutlined /> {t('adminView.menu.mdlv', 'Model View')}
					</Link>
				),
				key: 'modelview',
				className: classes.leftItems,
		  }
		: null;

	const items = [
		{
			label: (
				<Link className={classes.logo} to='/'>
					<img src={paLogo} width='90' alt='logo' />
				</Link>
			),
			key: '/',
			className: classes.leftItems,
		},
		{
			label: (
				<div className={classes.space}>
					<span>{displayConnectionStatus()}</span>
				</div>
			),
			key: 'connectionStatus',
			className: classes.leftItems,
		},
		projectItem,
		modelOrProjectView,
		activeUserItem,
		{
			label:
				!user || (Object.keys(user).length === 0 && user.constructor === Object) ? (
					<Link to={`/login?redirect=${location.pathname}`} className={classes.textColor}>
						<span>{t('navbar.login', 'Login')} </span>
					</Link>
				) : (
					<MyAvatar
						// user name avatar in top right corner
						userId={user._id}
						username={user.name}
						adminAcess={user.adminAccess}
						models={modelList.items}
					/>
				),
			key: 'avatar',
			className: classes.rightItems,
		},
	];

	return (
		<Menu
			style={{ background: colors.model_TopBack }}
			selectedKeys={[activeTab]}
			mode='horizontal'
			theme='dark'
			className={classes.menu}
			items={items}
		/>
	);
};

const mapStateToProps = (state) => ({
	activeUser: state.activeUser,
	modelList: state.modelList,
	activeModel: state.activeModel,
	settings: state.settings,
});

export default connect(mapStateToProps)(NormalNavbar);
